// @owners { team: patients }
import { COLORS } from '@alto/design-library-tokens';
import {
  Button,
  Card,
  InputRadio,
  InputRadioGroup,
  LgPadding,
  LoadingEmptyState,
  SecondaryPage,
  Toast,
  ToastContext,
  XlPadding,
} from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { InfoRequestHeader } from './InfoRequestHeader';
import { type HubReferralRouteParams } from './LandingPage';
import { NavBarLogo } from './NavBarLogo';
import { useCreateCommsConfig, useQueryCommsConfigPageContent } from '~web/features/info-request/hooks';
import { useStatusBarColor } from '~web/features/info-request/hooks/useStatusBarColor';

export const CommsConfigPage = () => {
  const { hubReferralID: hubReferralIDParam } = useParams<HubReferralRouteParams>();
  const hubReferralID = Number(hubReferralIDParam || 0);
  const [value, setValue] = useState<string | null>(null);
  const { goBack, navigate } = useNavigation();
  const { trackEvent, trackPageView } = useAnalytics();
  const { addToast } = useContext(ToastContext);
  const { isFetching, headerLogoUrl, pageBody, pageHeader, nextURL, redirectURL } = useQueryCommsConfigPageContent({
    hubReferralID,
  });
  const { mutateAsync: createCommsConfig } = useCreateCommsConfig();
  const [loading, setLoading] = useState(false);

  const handleValueChange = (value: string) => {
    setValue(value);
  };

  const handleGoBack = () => {
    goBack();
  };

  const handleSubmit = async () => {
    const params = {
      hub_referral_id: hubReferralID,
      has_sms_opted_out: value === 'no',
    };

    trackEvent({
      event: EVENTS.INFO_REQUEST__SUBMIT_SMS_CONSENT_CLICKED,
      params: {
        hub_referral_id: hubReferralID,
        has_sms_opted_out: value === 'no',
        next_url: nextURL,
      },
    });
    setLoading(true);
    const response = await createCommsConfig(params);
    if (response.errors) {
      addToast(<Toast variant="error">Failed to save messaging preferences. Please try again.</Toast>);
      trackEvent({
        event: EVENTS.INFO_REQUEST__SUBMIT_SMS_CONSENT_FAILED,
        params: {
          hub_referral_id: hubReferralID,
          has_sms_opted_out: value === 'no',
          next_url: nextURL,
        },
      });
    } else {
      addToast(<Toast variant="success">Messaging preferences successfully saved.</Toast>);
      trackEvent({
        event: EVENTS.INFO_REQUEST__SUBMIT_SMS_CONSENT_SUCCEED,
        params: {
          hub_referral_id: hubReferralID,
          has_sms_opted_out: value === 'no',
          next_url: nextURL,
        },
      });
    }

    if (nextURL) {
      navigate(nextURL);
    }
    setLoading(false);
  };

  useStatusBarColor(COLORS.BACKGROUND_COLORS.GREY_LIGHTEST);

  useEffect(() => {
    trackPageView({
      event: EVENTS.INFO_REQUEST__COMMS_CONFIG_PAGE_VIEWED,
      params: { hubReferralID },
    });
  }, [trackPageView, hubReferralID]);

  useEffect(() => {
    if (redirectURL) {
      navigate(redirectURL);
    }
  }, [navigate, redirectURL]);

  if (isFetching) {
    return (
      <XlPadding>
        <LoadingEmptyState />
      </XlPadding>
    );
  }

  if (!pageHeader) {
    return null;
  }

  return (
    <SecondaryPage
      footerPlacementContext="webScreenWithoutPolicyFooter"
      withoutWebFramingElementHeights
      headerBackgroundColor={COLORS.BACKGROUND_COLORS.GREY_LIGHTEST}
      dismissIcon="chevronleft"
      onDismiss={handleGoBack}
      NavBarCenterContent={headerLogoUrl ? <NavBarLogo illustrationSrc={headerLogoUrl} /> : undefined}
      HeaderContent={
        <InfoRequestHeader
          title={pageHeader}
          subtitle={pageBody}
        />
      }
      buttons={[
        <Button
          label="Submit"
          key="submit"
          onPress={handleSubmit}
          disabled={!value && loading}
        />,
      ]}
    >
      <Card>
        <LgPadding>
          <InputRadioGroup
            onValueChange={handleValueChange}
            defaultValue="1"
          >
            <InputRadio
              key="yes"
              label="Yes"
              value="yes"
            />
            <InputRadio
              key="no"
              label="No"
              value="no"
            />
          </InputRadioGroup>
        </LgPadding>
      </Card>
    </SecondaryPage>
  );
};
